import { Controller } from "@hotwired/stimulus"

// Generic multi-select pulldown rendered as a single-line dropdown
// trigger that opens a checkbox panel. Drop-in replacement for the
// native `<select multiple>` listbox — the hidden inputs the panel
// renders carry the selected values, so the surrounding form treats
// the picker like any other multi-value field.
//
// Used on the CIP Forecast page for the Fund Sources filter; the
// controller is intentionally generic so any future "pick a subset
// from a list" filter can adopt it without a bespoke controller.
//
// Submit behavior: clicks inside the panel update the trigger label
// but DON'T fire the surrounding form's change-submit (we stop
// propagation on the checkbox change event). When the user clicks
// outside / presses Escape, if the selection changed since open we
// request a single form submit. Net effect: exactly one form roundtrip
// regardless of how many checkboxes were toggled.
export default class extends Controller {
  static targets = ["label", "panel", "checkbox", "selectAll"]
  static values = {
    allLabel: { type: String, default: "All" },
    noneLabel: { type: String, default: "None" },
    singular: { type: String, default: "item" },
    plural:   { type: String, default: "items" }
  }

  connect() {
    this._onDocClick = (e) => {
      if (!this.element.contains(e.target)) this._close()
    }
    this._onKey = (e) => { if (e.key === "Escape") this._close() }
    document.addEventListener("click", this._onDocClick)
    document.addEventListener("keydown", this._onKey)

    this._updateLabel()
    this._updateSelectAll()
  }

  disconnect() {
    document.removeEventListener("click", this._onDocClick)
    document.removeEventListener("keydown", this._onKey)
  }

  toggle(event) {
    event?.preventDefault?.()
    event?.stopPropagation?.()
    if (!this.hasPanelTarget) return
    this.panelTarget.classList.contains("hidden") ? this._open() : this._close()
  }

  // Per-checkbox change inside the panel. Update label, keep "Select
  // all" indeterminate state in sync, but don't let the change event
  // bubble to the surrounding form (which would submit per click).
  itemChanged(event) {
    event.stopPropagation()
    this._dirty = true
    this._updateLabel()
    this._updateSelectAll()
  }

  toggleAll(event) {
    event.stopPropagation()
    const target = event.currentTarget
    const checked = target.checked
    this.checkboxTargets.forEach(cb => { cb.checked = checked })
    this._dirty = true
    this._updateLabel()
    this._updateSelectAll()
  }

  _open() {
    if (!this.hasPanelTarget) return
    this.panelTarget.classList.remove("hidden")
    this._dirty = false
    this._snapshotSelection = this._currentSelection()
  }

  _close() {
    if (!this.hasPanelTarget) return
    if (this.panelTarget.classList.contains("hidden")) return
    this.panelTarget.classList.add("hidden")

    // Only submit if the actual checkbox set changed since the panel
    // opened — re-opening + closing without changes shouldn't reload.
    const changed = this._dirty && !this._sameSelection(this._snapshotSelection, this._currentSelection())
    if (changed) {
      const form = this.element.closest("form")
      if (form && typeof form.requestSubmit === "function") {
        form.requestSubmit()
      } else if (form) {
        form.submit()
      }
    }
  }

  _currentSelection() {
    return this.checkboxTargets.filter(cb => cb.checked).map(cb => cb.value).sort()
  }

  _sameSelection(a, b) {
    if (a.length !== b.length) return false
    for (let i = 0; i < a.length; i++) { if (a[i] !== b[i]) return false }
    return true
  }

  _updateLabel() {
    if (!this.hasLabelTarget) return
    const total = this.checkboxTargets.length
    const checked = this.checkboxTargets.filter(cb => cb.checked).length

    let text
    if (total === 0) {
      text = this.noneLabelValue
    } else if (checked === 0) {
      text = this.noneLabelValue
    } else if (checked === total) {
      text = `${this.allLabelValue} (${total})`
    } else if (checked === 1) {
      const only = this.checkboxTargets.find(cb => cb.checked)
      text = this._optionLabel(only) || `1 ${this.singularValue}`
    } else {
      text = `${checked} ${this.pluralValue}`
    }
    this.labelTarget.textContent = text
  }

  _optionLabel(checkbox) {
    if (!checkbox) return null
    if (checkbox.dataset.label) return checkbox.dataset.label
    const wrapper = checkbox.closest("label")
    if (!wrapper) return null
    const span = wrapper.querySelector("[data-multi-checkbox-picker-label]")
    return (span || wrapper).textContent.trim()
  }

  _updateSelectAll() {
    if (!this.hasSelectAllTarget) return
    const total = this.checkboxTargets.length
    const checked = this.checkboxTargets.filter(cb => cb.checked).length
    this.selectAllTarget.checked = total > 0 && checked === total
    this.selectAllTarget.indeterminate = checked > 0 && checked < total
  }
}
